import axios from 'axios'
import { ElMessage } from 'element-plus'
import router from '../router'
const { API_URL } = require('../../config')

/** 登录失效、需重新登录时的统一提示（与拦截器、reject 错误对象一致） */
export const MSG_SESSION_EXPIRED = '登录已过期，请重新登录'

/**
 * 将 axios 错误转为会话失效错误，供业务 catch 中识别并避免重复弹窗 / 避免展示「Request failed with status code 401」
 * @param {*} originalError axios 错误对象
 */
export function toAuthExpiredError (originalError) {
  const err = new Error(MSG_SESSION_EXPIRED)
  err.isAuthExpired = true
  err.response = originalError && originalError.response
  return err
}

/**
 * 后端返回 message 是否为「需重新登录」类说明（兼容多种措辞）
 * @param {unknown} msg
 */
function isTokenExpiredLikeMessage (msg) {
  if (typeof msg !== 'string' || !msg.trim()) return false
  const t = msg.trim()
  if (t === '令牌无效' || t === '令牌已过期' || t === '未提供访问令牌' || t === '令牌无效或已过期') return true
  if (t.includes('令牌') && (t.includes('过期') || t.includes('无效'))) return true
  if (t.includes('登录') && t.includes('过期')) return true
  if (t.includes('认证') && (t.includes('失败') || t.includes('过期'))) return true
  return false
}

// 创建axios实例
const http = axios.create({
  // 直接指向后端API服务器地址
  baseURL: API_URL,
  timeout: 10000, // 请求超时时间
  headers: {
    'Content-Type': 'application/json'
  },
  // 添加跨域支持
  withCredentials: false
})

// 请求拦截器
http.interceptors.request.use(
  config => {
    // 获取token
    const token = localStorage.getItem('token')
    console.log('请求拦截器 - 请求URL:', config.url)
    // 如果URL已经是完整URL（以http://或https://开头），则不拼接baseURL
    const fullUrl = config.url && (config.url.startsWith('http://') || config.url.startsWith('https://'))
      ? config.url
      : `${config.baseURL || ''}${config.url || ''}`
    console.log('请求拦截器 - 完整URL:', fullUrl)
    console.log('请求拦截器 - 请求方法:', config.method)
    console.log('请求拦截器 - 当前token:', token ? '已设置' : '未设置')
    
    // 如果有token，添加到请求头
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
      console.log('请求拦截器 - 添加token到请求头')
    }
    
    // 如果是 FormData，删除 Content-Type 让浏览器自动设置（包含 boundary）
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
      console.log('检测到 FormData，删除 Content-Type 头部')
    }
    
    return config
  },
  error => {
    console.error('请求拦截器错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
http.interceptors.response.use(
  response => {
    // 如果返回的是文件流，直接返回
    if (response.config.responseType === 'blob') {
      return response
    }
    
    // 检查响应数据
    console.log('响应数据:', response.data)
    
    // 如果响应数据是对象，直接返回
    if (typeof response.data === 'object' && response.data !== null) {
      return response.data
    }
    
    // 如果响应数据是字符串，尝试解析为JSON
    if (typeof response.data === 'string') {
      try {
        const parsedData = JSON.parse(response.data)
        console.log('解析后的响应数据:', parsedData)
        return parsedData
      } catch (error) {
        console.error('解析响应数据失败:', error)
        return response.data
      }
    }
    
    // 其他情况直接返回
    return response.data
  },
  error => {
    console.error('响应错误:', error)
    
    // 检查是否是超时错误
    if (error.code === 'ECONNABORTED' && error.message.includes('timeout')) {
      const timeout = error.config?.timeout || 10000
      const timeoutSeconds = timeout / 1000
      console.error('请求超时:', {
        url: error.config?.url,
        timeout: `${timeoutSeconds}秒`,
        message: error.message
      })
      
      // 如果是文件上传，给出更明确的提示
      if (error.config?.data instanceof FormData) {
        ElMessage.error(`文件上传超时（${timeoutSeconds}秒），文件可能较大，请稍后重试或联系管理员`)
      } else {
        ElMessage.error(`请求超时（${timeoutSeconds}秒），请稍后重试`)
      }
      return Promise.reject(new Error(`请求超时（${timeoutSeconds}秒）`))
    }
    
    if (error.response) {
      // 服务器返回了错误状态码
      const status = error.response.status
      const isStoryboard404 = (
        error.config?.url?.includes('/api/video/storyboard/') ||
        error.config?.url?.includes('/api/aivideo/storyboard/')
      ) && status === 404
      
      // 404 错误对于分镜数据是正常的（视频可能没有分镜数据），不需要显示错误日志
      if (isStoryboard404) {
        // 静默处理，不输出错误日志
        return Promise.reject(error)
      }
      
      console.error('错误状态码:', status)
      console.error('错误信息:', error.response.data)

      const msg = error.response.data?.message
      const isLoginApi = error.config && error.config.url && error.config.url.includes('/api/auth/login')
      // 分镜分享查看接口的401错误是密码错误，不应该跳转到登录页面
      const isStoryboardShareViewApi = error.config && error.config.url && (
        error.config.url.includes('/api/video/storyboard/share/view/') ||
        error.config.url.includes('/api/aivideo/storyboard/share/view/')
      )
      const tokenExpiredMsg = isTokenExpiredLikeMessage(msg)
      const shouldForceRelogin =
        (error.response.status === 401 && !isLoginApi && !isStoryboardShareViewApi) ||
        (tokenExpiredMsg && !isLoginApi && !isStoryboardShareViewApi)
      if (shouldForceRelogin) {
        // token 失效或未登录：提示后清理并跳转登录（非登录接口、非分镜分享查看接口）
        ElMessage.warning(MSG_SESSION_EXPIRED)
        localStorage.removeItem('token')
        localStorage.removeItem('username')
        localStorage.removeItem('roles')
        localStorage.removeItem('permissions')
        console.log('will jump to login')
        try {
          router.replace('/login').catch(() => {})
        } catch (e) {
          window.location.href = '/login'
        }
        return Promise.reject(toAuthExpiredError(error))
      }
      if (error.response.status === 403) {
        if (tokenExpiredMsg) {
          ElMessage.warning(MSG_SESSION_EXPIRED)
          localStorage.removeItem('token')
          localStorage.removeItem('username')
          localStorage.removeItem('roles')
          localStorage.removeItem('permissions')
          console.log('will jump to login')
          try {
            router.replace('/login').catch(() => {})
          } catch (e) {
            window.location.href = '/login'
          }
          return Promise.reject(toAuthExpiredError(error))
        }
        ElMessage.error(msg || '无权限操作')
      }
      return Promise.reject(error)
    } else if (error.request) {
      // 请求已发出但没有收到响应
      console.error('请求错误:', error.request)
      console.error('请求配置:', error.config)
      
      // 检查是否是 status 0 错误（通常是 CORS 或网络连接问题）
      if (error.request instanceof XMLHttpRequest && error.request.status === 0) {
        const requestUrl = error.config ? `${error.config.baseURL}${error.config.url}` : '未知URL'
        const errorMsg = `网络连接失败 (Status 0)\n请求URL: ${requestUrl}\n\n可能的原因:\n1. 后端服务未运行 (${API_URL})\n2. CORS 跨域问题\n3. 网络连接失败\n4. 防火墙阻止`
        
        console.error('Status 0 错误详情:', {
          readyState: error.request.readyState,
          responseURL: error.request.responseURL || '无响应URL',
          status: error.request.status,
          statusText: error.request.statusText,
          requestURL: requestUrl,
          baseURL: error.config?.baseURL,
          method: error.config?.method
        })
        
        // 尝试检查后端服务是否可达
        console.warn('建议检查后端服务状态:', API_URL)
        ElMessage.error(`网络连接失败，无法连接到 ${API_URL}`)
        return Promise.reject(new Error(errorMsg))
      }
      
      return Promise.reject(new Error('网络错误，请检查网络连接'))
    } else {
      // 请求配置出错
      console.error('请求配置错误:', error.message)
      return Promise.reject(error)
    }
  }
)

// 封装GET请求
export const get = (url, params = {}, config = {}) => {
  return http.get(url, { ...config, params })
}

// 封装POST请求
export const post = (url, data = {}, config = {}) => {
  return http.post(url, data, config)
}

// 封装PUT请求
export const put = (url, data = {}, config = {}) => {
  return http.put(url, data, config)
}

// 封装DELETE请求
export const del = (url, params = {}, config = {}) => {
  return http.delete(url, { ...config, params })
}

// 封装上传文件请求
export const upload = (url, file) => {
  const formData = new FormData()
  formData.append('file', file)
  
  return http.post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  })
}

// 封装下载文件请求
export const download = (url, params = {}, fileName = '') => {
  return http.get(url, {
    params,
    responseType: 'blob'
  }).then(response => {
    // 获取文件名
    const disposition = response.headers['content-disposition']
    let downloadFileName = fileName
    
    if (!downloadFileName && disposition) {
      const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = filenameRegex.exec(disposition)
      if (matches != null && matches[1]) {
        downloadFileName = matches[1].replace(/['"]/g, '')
      }
    }
    
    // 创建下载链接
    const blob = new Blob([response.data])
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.download = downloadFileName
    link.click()
    window.URL.revokeObjectURL(link.href)
  })
}

// 导出 http 实例，以便直接使用 axios 的方法（如 request）
// 注意：get, post, put, del, upload, download 已经在上面通过 export const 导出了
export default http 
